import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  Menu,
  MenuItem,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import {
  MdFileDownload,
  MdKeyboardArrowDown,
  MdChevronLeft,
  MdChevronRight,
} from 'react-icons/md';
import { useOutletContext } from 'react-router-dom';
import theme from './theme';
import api from '../utils/api';
import { getClientEmail } from '../utils/getClientEmail';
import DashboardInsightCard from './DashboardInsightCard';
import {
  pickDefaultPeriodKey,
  stepPeriod,
  canStepPeriod,
  formatPeriodNavigatorLabel,
} from './operationsPeriodUtils';

const EMPTY_CHART = { daily: [], weekly: [], monthly: [] };

/** Set true to show Method column in Session Log table and CSV export. */
const SHOW_SESSION_LOG_METHOD_COLUMN = false;

const SESSION_LOG_HEADERS = SHOW_SESSION_LOG_METHOD_COLUMN
  ? ['Date', 'Room', 'Type', 'Start', 'End', 'Duration', 'Method']
  : ['Date', 'Room', 'Type', 'Start', 'End', 'Duration'];

/** Toolbar / controls — match client Operations mockup (cream card, grey segment, dropdown triggers). */
const OPS = {
  text: '#333333',
  muted: '#888888',
  border: '#DDDDDD',
  segmentSelected: '#E8E8E8',
  radius: '8px',
};

/** Insights callout — lavender card + circular icon (matches Operations mockup). */
const CHART_PX_PER_CATEGORY = 72;
const CHART_AXIS_GUTTER = 96;

function toolbarSelectButtonSx(open) {
  return {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.875rem',
    color: OPS.text,
    bgcolor: '#fff',
    border: `1px solid ${OPS.border}`,
    borderRadius: OPS.radius,
    px: 2,
    py: 0.75,
    minWidth: 148,
    boxShadow: 'none',
    '&:hover': { bgcolor: '#fafafa', borderColor: OPS.border },
    ...(open && { bgcolor: '#fafafa' }),
  };
}

function sessionLogRowCells(s) {
  const base = [s.date, s.room, s.type, s.start, s.end, s.duration];
  if (SHOW_SESSION_LOG_METHOD_COLUMN) base.push(s.method);
  return base;
}

export default function OperationsPage() {
  const { clientEmail } = useOutletContext();
  const [granularity, setGranularity] = useState('monthly');
  const [chartSeries, setChartSeries] = useState(EMPTY_CHART);
  const [periodDetail, setPeriodDetail] = useState(null);
  const [selectedPeriodKey, setSelectedPeriodKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [periodLoading, setPeriodLoading] = useState(false);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [dateMenuAnchor, setDateMenuAnchor] = useState(null);
  const [chartScrollMeta, setChartScrollMeta] = useState({
    overflow: false,
    canLeft: false,
    canRight: false,
  });
  const chartScrollRef = useRef(null);
  const periodFetchId = useRef(0);

  const fetchChart = useCallback(async (rangeOverride) => {
    setLoading(true);
    try {
      const email = clientEmail || getClientEmail();
      const startDate = rangeOverride ? rangeOverride.startDate : dateRange.start;
      const endDate = rangeOverride ? rangeOverride.endDate : dateRange.end;
      const params = { email, includeChart: true, startDate, endDate };
      const res = await api.get('/client/operations', { params });
      if (res.data?.success) {
        setChartSeries(res.data.chart || EMPTY_CHART);
      } else {
        setChartSeries(EMPTY_CHART);
      }
    } catch (e) {
      console.error('Operations chart fetch error:', e);
      setChartSeries(EMPTY_CHART);
    } finally {
      setLoading(false);
    }
  }, [dateRange, clientEmail]);

  const fetchPeriodDetail = useCallback(
    async (periodKey, gran) => {
      const email = clientEmail || getClientEmail();
      if (!email || !periodKey) {
        setPeriodDetail(null);
        return;
      }
      const requestId = ++periodFetchId.current;
      setPeriodLoading(true);
      try {
        const res = await api.get('/client/operations', {
          params: {
            email,
            includeChart: false,
            granularity: gran,
            periodKey,
          },
        });
        if (requestId !== periodFetchId.current) return;
        if (res.data?.success) {
          setPeriodDetail(res.data);
        } else {
          setPeriodDetail(null);
        }
      } catch (e) {
        console.error('Operations period detail fetch error:', e);
        if (requestId === periodFetchId.current) setPeriodDetail(null);
      } finally {
        if (requestId === periodFetchId.current) setPeriodLoading(false);
      }
    },
    [clientEmail]
  );

  useEffect(() => {
    fetchChart();
  }, [fetchChart]);

  const displayChartData = useMemo(() => {
    const series = chartSeries?.[granularity];
    return Array.isArray(series) ? series : [];
  }, [chartSeries, granularity]);

  useEffect(() => {
    if (!displayChartData.length) {
      setSelectedPeriodKey('');
      setPeriodDetail(null);
      return;
    }
    setSelectedPeriodKey((prev) => {
      const keys = new Set(displayChartData.map((r) => r.key));
      if (prev && keys.has(prev)) return prev;
      return pickDefaultPeriodKey(granularity, displayChartData);
    });
  }, [displayChartData, granularity]);

  useEffect(() => {
    if (!selectedPeriodKey) return;
    fetchPeriodDetail(selectedPeriodKey, granularity);
  }, [selectedPeriodKey, granularity, fetchPeriodDetail]);

  const updateChartScrollMeta = useCallback(() => {
    const el = chartScrollRef.current;
    if (!el) return;
    const overflow = el.scrollWidth > el.clientWidth + 1;
    setChartScrollMeta({
      overflow,
      canLeft: overflow && el.scrollLeft > 1,
      canRight: overflow && el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
    });
  }, []);

  useEffect(() => {
    const el = chartScrollRef.current;
    if (!el) return;
    el.scrollLeft = el.scrollWidth - el.clientWidth;
    updateChartScrollMeta();
    el.addEventListener('scroll', updateChartScrollMeta);
    window.addEventListener('resize', updateChartScrollMeta);
    return () => {
      el.removeEventListener('scroll', updateChartScrollMeta);
      window.removeEventListener('resize', updateChartScrollMeta);
    };
  }, [displayChartData, granularity, updateChartScrollMeta]);

  const scrollChartBy = (direction) => {
    const el = chartScrollRef.current;
    if (!el) return;
    const delta = Math.round(el.clientWidth * 0.75) * direction;
    el.scrollTo({ left: el.scrollLeft + delta, behavior: 'smooth' });
  };

  const handleGranularityChange = (_, val) => {
    if (val) setGranularity(val);
  };

  const handleBarClick = (entry) => {
    const payload = entry?.activePayload?.[0]?.payload;
    if (payload?.key) setSelectedPeriodKey(payload.key);
  };

  const sessionLog = periodDetail?.sessionLog || [];
  const insightsFromView = periodDetail?.insightsFromView ?? null;
  const insightLegacy =
    typeof periodDetail?.insight === 'string' && periodDetail.insight.trim()
      ? periodDetail.insight.trim()
      : null;

  const handleExportCsv = () => {
    const rows = sessionLog.map((s) => sessionLogRowCells(s));
    const csv = [SESSION_LOG_HEADERS, ...rows].map((r) => r.join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `operations_${granularity}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const periodWord = granularity === 'monthly' ? 'months' : granularity === 'weekly' ? 'weeks' : 'days';
  const periodWordSingular = granularity === 'monthly' ? 'month' : granularity === 'weekly' ? 'week' : 'day';
  const nBars = displayChartData.length;
  const selectedPeriodLabel = formatPeriodNavigatorLabel(selectedPeriodKey, granularity);
  const canGoBack = canStepPeriod(displayChartData, selectedPeriodKey, -1);
  const canGoForward = canStepPeriod(displayChartData, selectedPeriodKey, 1);
  const prevPeriodLabel = useMemo(() => {
    const k = stepPeriod(displayChartData, selectedPeriodKey, -1);
    return k && k !== selectedPeriodKey ? formatPeriodNavigatorLabel(k, granularity) : null;
  }, [displayChartData, selectedPeriodKey, granularity]);
  const nextPeriodLabel = useMemo(() => {
    const k = stepPeriod(displayChartData, selectedPeriodKey, 1);
    return k && k !== selectedPeriodKey ? formatPeriodNavigatorLabel(k, granularity) : null;
  }, [displayChartData, selectedPeriodKey, granularity]);

  const applyDateRange = () => {
    fetchChart();
    setDateMenuAnchor(null);
  };

  const clearDateRange = () => {
    setDateRange({ start: '', end: '' });
    fetchChart({ startDate: '', endDate: '' });
    setDateMenuAnchor(null);
  };

  const hasDateFilter = Boolean(dateRange.start || dateRange.end);

  const chartMinInnerWidth = useMemo(() => {
    const n = displayChartData?.length ?? 0;
    return Math.max(360, n * CHART_PX_PER_CATEGORY + CHART_AXIS_GUTTER);
  }, [displayChartData]);

  const operationsInsightSource = useMemo(() => {
    const hasPoints = Array.isArray(insightsFromView?.points) && insightsFromView.points.length > 0;
    const hasNarrative =
      typeof insightsFromView?.narrative === 'string' && insightsFromView.narrative.trim().length > 0;
    if (insightsFromView?.source === 'ai' || insightsFromView?.source === 'template') {
      return insightsFromView.source;
    }
    return hasPoints || hasNarrative ? 'ai' : 'template';
  }, [insightsFromView]);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: { xs: 'wrap', lg: 'nowrap' },
          mb: 2.5,
        }}
      >
        <ToggleButtonGroup
          value={granularity}
          exclusive
          onChange={handleGranularityChange}
          size="small"
          sx={{
            order: { xs: 1, lg: 0 },
            flexShrink: 0,
            border: `1px solid ${OPS.border}`,
            borderRadius: OPS.radius,
            overflow: 'hidden',
            bgcolor: '#fff',
            '& .MuiToggleButtonGroup-grouped': {
              margin: 0,
              border: 0,
              borderRadius: 0,
              '&:not(:last-of-type)': {
                borderRight: `1px solid ${OPS.border}`,
              },
            },
            '& .MuiToggleButton-root': {
              px: 2,
              py: 0.75,
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              color: OPS.muted,
              bgcolor: '#fff',
              '&.Mui-selected': {
                color: OPS.text,
                fontWeight: 600,
                bgcolor: OPS.segmentSelected,
                '&:hover': { bgcolor: OPS.segmentSelected },
              },
            },
          }}
        >
          <ToggleButton value="monthly">Monthly</ToggleButton>
          <ToggleButton value="weekly">Weekly</ToggleButton>
          <ToggleButton value="daily">Daily</ToggleButton>
        </ToggleButtonGroup>

        <Typography
          component="div"
          sx={{
            flex: 1,
            minWidth: 0,
            textAlign: { xs: 'left', lg: 'center' },
            fontSize: '0.8125rem',
            color: OPS.muted,
            lineHeight: 1.5,
            px: { xs: 0, lg: 1 },
            order: { xs: 3, lg: 0 },
            width: { xs: '100%', lg: 'auto' },
          }}
        >
          All {nBars} {periodWord} in range · tap a bar to view that period below
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, ml: { xs: 0, lg: 'auto' }, order: { xs: 2, lg: 0 } }}>
          <Button
            disableElevation
            endIcon={<MdKeyboardArrowDown size={18} />}
            onClick={(e) => setDateMenuAnchor(e.currentTarget)}
            sx={toolbarSelectButtonSx(Boolean(dateMenuAnchor))}
          >
            Date range
          </Button>
          <Menu
            anchorEl={dateMenuAnchor}
            open={Boolean(dateMenuAnchor)}
            onClose={() => setDateMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{ paper: { sx: { borderRadius: OPS.radius, border: `1px solid ${OPS.border}`, mt: 0.5, minWidth: 260 } } }}
          >
            <Box sx={{ px: 2, pt: 2, pb: 1 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: OPS.muted, mb: 1 }}>Start</Typography>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange((p) => ({ ...p, start: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: `1px solid ${OPS.border}`,
                  borderRadius: OPS.radius,
                  fontSize: 13,
                  color: OPS.text,
                }}
              />
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: OPS.muted, mb: 1, mt: 1.5 }}>End</Typography>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange((p) => ({ ...p, end: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: `1px solid ${OPS.border}`,
                  borderRadius: OPS.radius,
                  fontSize: 13,
                  color: OPS.text,
                }}
              />
            </Box>
            <Divider />
            <MenuItem
              onClick={clearDateRange}
              disabled={!hasDateFilter}
              sx={{ justifyContent: 'center', fontSize: 13, color: OPS.muted, '&.Mui-disabled': { opacity: 0.5 } }}
            >
              Clear dates
            </MenuItem>
            <MenuItem onClick={applyDateRange} sx={{ justifyContent: 'center', fontWeight: 600, color: theme.colors.brandPrimary }}>
              Apply
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: theme.colors.brandPrimary }} />
        </Box>
      ) : (
        <>
          <Paper elevation={0} sx={{ p: 3, borderRadius: `${theme.radius.lg}px`, border: `1px solid ${theme.colors.borderDefault}`, mb: 2 }}>
            <Typography sx={{ fontSize: 12, color: theme.colors.textSecondary, mb: 1.5 }}>
              Tap a bar to view that period below
            </Typography>
            <Box sx={{ position: 'relative' }}>
              {chartScrollMeta.overflow ? (
                <>
                  <Button
                    size="small"
                    variant="contained"
                    aria-label="Scroll chart left"
                    disabled={!chartScrollMeta.canLeft}
                    onClick={(e) => {
                      e.stopPropagation();
                      scrollChartBy(-1);
                    }}
                    sx={{
                      position: 'absolute',
                      left: 4,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 2,
                      minWidth: 36,
                      width: 36,
                      height: 36,
                      p: 0,
                      borderRadius: '50%',
                      bgcolor: 'rgba(255,255,255,0.92)',
                      color: theme.colors.textPrimary,
                      boxShadow: '0 2px 8px rgba(15,23,42,0.15)',
                      border: `1px solid ${theme.colors.borderDefault}`,
                      '&:hover': { bgcolor: '#fff' },
                      '&.Mui-disabled': { opacity: 0.35 },
                    }}
                  >
                    <MdChevronLeft size={22} />
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    aria-label="Scroll chart right"
                    disabled={!chartScrollMeta.canRight}
                    onClick={(e) => {
                      e.stopPropagation();
                      scrollChartBy(1);
                    }}
                    sx={{
                      position: 'absolute',
                      right: 4,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 2,
                      minWidth: 36,
                      width: 36,
                      height: 36,
                      p: 0,
                      borderRadius: '50%',
                      bgcolor: 'rgba(255,255,255,0.92)',
                      color: theme.colors.textPrimary,
                      boxShadow: '0 2px 8px rgba(15,23,42,0.15)',
                      border: `1px solid ${theme.colors.borderDefault}`,
                      '&:hover': { bgcolor: '#fff' },
                      '&.Mui-disabled': { opacity: 0.35 },
                    }}
                  >
                    <MdChevronRight size={22} />
                  </Button>
                  {chartScrollMeta.canLeft ? (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 8,
                        width: 48,
                        zIndex: 1,
                        pointerEvents: 'none',
                        background: 'linear-gradient(to right, rgba(255,255,255,0.95), transparent)',
                      }}
                    />
                  ) : null}
                  {chartScrollMeta.canRight ? (
                    <Box
                      sx={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 8,
                        width: 48,
                        zIndex: 1,
                        pointerEvents: 'none',
                        background: 'linear-gradient(to left, rgba(255,255,255,0.95), transparent)',
                      }}
                    />
                  ) : null}
                </>
              ) : null}
              <Box
                ref={chartScrollRef}
                sx={{
                  width: '100%',
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  WebkitOverflowScrolling: 'touch',
                  pb: 0.5,
                  '&::-webkit-scrollbar': { height: 8 },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(15, 23, 42, 0.2)',
                    borderRadius: 4,
                  },
                }}
              >
              <Box sx={{ width: `max(100%, ${chartMinInnerWidth}px)`, height: 320, minHeight: 320 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={displayChartData}
                    margin={{ top: 8, right: 12, left: 4, bottom: 4 }}
                    onClick={handleBarClick}
                    style={{ cursor: 'pointer' }}
                    barCategoryGap="18%"
                    barGap={4}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.borderDefault} />
                    <XAxis
                      dataKey="label"
                      interval={0}
                      tick={{ fontSize: 11, fill: theme.colors.textSecondary }}
                      height={36}
                    />
                    <YAxis tick={{ fontSize: 12, fill: theme.colors.textSecondary }} width={44} />
                    <Tooltip />
                    <Bar dataKey="hours" radius={[4, 4, 0, 0]} name="Hours">
                      {displayChartData.map((entry, index) => {
                        const active = entry.key === selectedPeriodKey;
                        return (
                          <Cell
                            key={`hours-${entry.key}-${index}`}
                            fill={active ? theme.colors.brandPrimary : `${theme.colors.brandPrimary}99`}
                            stroke={active ? '#0f172a' : 'none'}
                            strokeWidth={active ? 3 : 0}
                            strokeDasharray={active ? '6 2' : undefined}
                          />
                        );
                      })}
                    </Bar>
                    <Bar dataKey="sessions" radius={[4, 4, 0, 0]} name="Sessions">
                      {displayChartData.map((entry, index) => {
                        const active = entry.key === selectedPeriodKey;
                        return (
                          <Cell
                            key={`sessions-${entry.key}-${index}`}
                            fill={active ? theme.colors.accentSuccess : `${theme.colors.accentSuccess}99`}
                            stroke={active ? '#0f172a' : 'none'}
                            strokeWidth={active ? 3 : 0}
                            strokeDasharray={active ? '6 2' : undefined}
                          />
                        );
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Box>
            </Box>
          </Paper>

          <Box
            role="navigation"
            aria-label={`${periodWordSingular} navigator`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1.5,
              mb: 3,
              p: 2,
              borderRadius: `${theme.radius.lg}px`,
              border: `1px solid ${theme.colors.borderDefault}`,
              bgcolor: theme.colors.bgSecondary,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: theme.colors.textSecondary, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                {`Viewing ${periodWordSingular}`}
              </Typography>
              <Typography sx={{ fontSize: 20, fontWeight: 700, color: theme.colors.textPrimary, lineHeight: 1.2 }}>
                {selectedPeriodLabel || '—'}
              </Typography>
              <Typography sx={{ fontSize: 12, color: theme.colors.textSecondary, mt: 0.25 }}>
                {granularity === 'daily'
                  ? `Tap a chart bar or use the arrows to view another ${periodWordSingular} — the Session Log below updates.`
                  : `Tap a chart bar or use the arrows to view another ${periodWordSingular} — the Session Log and insights below update.`}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<MdChevronLeft size={18} />}
                disabled={!canGoBack}
                onClick={() => setSelectedPeriodKey((k) => stepPeriod(displayChartData, k, -1))}
                aria-label={prevPeriodLabel ? `Previous ${periodWordSingular}: ${prevPeriodLabel}` : `Previous ${periodWordSingular}`}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: theme.colors.borderDefault,
                  color: theme.colors.textPrimary,
                  bgcolor: theme.colors.bgPrimary,
                  '&:hover': { bgcolor: theme.colors.bgSecondary, borderColor: theme.colors.brandPrimary },
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  {prevPeriodLabel || 'Previous'}
                </Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                  Prev
                </Box>
              </Button>
              <Button
                size="small"
                variant="outlined"
                endIcon={<MdChevronRight size={18} />}
                disabled={!canGoForward}
                onClick={() => setSelectedPeriodKey((k) => stepPeriod(displayChartData, k, 1))}
                aria-label={nextPeriodLabel ? `Next ${periodWordSingular}: ${nextPeriodLabel}` : `Next ${periodWordSingular}`}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: theme.colors.borderDefault,
                  color: theme.colors.textPrimary,
                  bgcolor: theme.colors.bgPrimary,
                  '&:hover': { bgcolor: theme.colors.bgSecondary, borderColor: theme.colors.brandPrimary },
                }}
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  {nextPeriodLabel || 'Next'}
                </Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                  Next
                </Box>
              </Button>
            </Box>
          </Box>

          {granularity !== 'daily' ? (
            <DashboardInsightCard
              title="Insights from this view"
              insightsFromView={insightsFromView}
              legacyInsight={insightLegacy}
              source={operationsInsightSource}
              loading={periodLoading}
              disclaimerMode="short"
              attributionLive="Generated by AI from your current operations view data for this period."
              attributionCached="Generated by AI from your operations view data for this period."
              adminCacheInvalidation={{
                surface: 'operations',
                cacheParams: {
                  email: clientEmail || getClientEmail() || '',
                  granularity,
                  periodKey: selectedPeriodKey,
                },
                onInvalidated: () => fetchPeriodDetail(selectedPeriodKey, granularity),
              }}
            />
          ) : null}

          <Paper elevation={0} sx={{ borderRadius: `${theme.radius.lg}px`, border: `1px solid ${theme.colors.borderDefault}`, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', p: 2.5, pb: 1.5, flexWrap: 'wrap', gap: 1 }}>
              <Box>
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: theme.colors.textPrimary }}>Session Log</Typography>
                {selectedPeriodLabel ? (
                  <Typography sx={{ fontSize: 12, color: theme.colors.textSecondary, mt: 0.25 }}>{selectedPeriodLabel}</Typography>
                ) : null}
              </Box>
              <Button size="small" startIcon={<MdFileDownload />} onClick={handleExportCsv} sx={{ textTransform: 'none', fontWeight: 600, color: theme.colors.brandPrimary }}>
                Export CSV
              </Button>
            </Box>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {SESSION_LOG_HEADERS.map((h) => (
                      <TableCell key={h} sx={{ bgcolor: theme.colors.textPrimary, color: '#fff', fontWeight: 600, fontSize: 12 }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {periodLoading ? (
                    <TableRow>
                      <TableCell colSpan={SESSION_LOG_HEADERS.length} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={24} sx={{ color: theme.colors.brandPrimary }} />
                      </TableCell>
                    </TableRow>
                  ) : sessionLog.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={SESSION_LOG_HEADERS.length} align="center" sx={{ py: 4, color: theme.colors.textSecondary }}>
                        No sessions in this period.
                      </TableCell>
                    </TableRow>
                  ) : (
                    sessionLog.map((s, i) => (
                      <TableRow key={i} sx={{ '&:nth-of-type(even)': { bgcolor: theme.colors.bgSecondary } }}>
                        {sessionLogRowCells(s).map((cell, j) => (
                          <TableCell key={j} sx={{ fontSize: 13, fontWeight: j === 1 ? 600 : 400 }}>
                            {cell}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
}
